import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import { AuthRepository } from "@/api/auth/repository";
import { AuthService } from "@/api/auth/service";
import { LS } from "@/constants/index";
import { useAppStore } from "@/store/app/slice";
import { AuthState } from "@/types/common";
import { Environment, getEnvironment } from "@/utils/getEnvironment";

let isRefreshing = false;
let failedRequestsQueue: Array<{
  config: InternalAxiosRequestConfig;
  resolve: (config: InternalAxiosRequestConfig) => void;
  reject: (error: AxiosError) => void;
}> = [];

export const onRefreshToken = (
  error: AxiosError,
): Promise<InternalAxiosRequestConfig> => {
  const authRepository = new AuthRepository(new AuthService());
  const originalRequest = error.config as InternalAxiosRequestConfig;

  return new Promise((resolve, reject) => {
    failedRequestsQueue.push({
      config: { ...originalRequest },
      resolve,
      reject,
    });

    if (originalRequest.url?.includes("/auth/refreshToken")) {
      isRefreshing = false;
      failedRequestsQueue = [];
      reject(error);
      return Promise.reject(error);
    }

    if (!isRefreshing) {
      isRefreshing = true;

      const refreshToken =
        getEnvironment() === Environment.Local
          ? localStorage.getItem(LS.RefreshToken)
          : null;

      authRepository
        .refreshToken({
          refreshToken,
        })
        .then(({ accessToken, refreshToken }) => {
          useAppStore.getState().setAccessToken(accessToken);
          useAppStore.getState().setAuthenticated(AuthState.Authenticated);
          if (getEnvironment() === Environment.Local && refreshToken) {
            localStorage.setItem(LS.RefreshToken, refreshToken);
          }
          failedRequestsQueue.forEach(({ config, resolve }) => {
            resolve(config);
          });
        })
        .catch((refreshError) => {
          failedRequestsQueue.forEach(({ reject }) => {
            reject(refreshError);
          });
          reject(refreshError);
        })
        .finally(() => {
          failedRequestsQueue = [];
          isRefreshing = false;
        });
    }
  });
};
